import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import LogoutModal from '../components/common/LogoutModal';
import LoadingModal from '../components/common/LoadingModal';
import { useSelector, useDispatch } from 'react-redux';
import { getResearchReport, generateOutline } from '../store/actions/researchReportActions';
import { MobileMenuIcon, SparkleIcon, BackIcon, StarrIcon } from '../assets/svgs/svgs';
import { logoutUser } from '../store/actions/authActions';
import { getUserInitials, getUserDisplayName } from '../utils/userUtils';

const ResearchResults = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { formData, researchId } = location.state || {};

  const [selectedTopic, setSelectedTopic] = useState(null);
  const [showTopicDetails, setShowTopicDetails] = useState(false);
  const [claimedTopics, setClaimedTopics] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 3;

  const user = useSelector((state) => state.auth.user);
  const researchReportState = useSelector((state) => state.researchReport);
  const suggestedTopics = researchReportState?.reportData?.suggestedTopics || [];

  useEffect(() => {
    if (researchId) {
      dispatch(getResearchReport(researchId));
    }
  }, [researchId, dispatch]);

  useEffect(() => {
    if (suggestedTopics.length > 0 && currentPage > Math.ceil(suggestedTopics.length / itemsPerPage)) {
      setCurrentPage(1);
    }
  }, [suggestedTopics.length]);

  const formatViews = (views) => {
    if (views >= 1000000) {
      return `${(views / 1000000).toFixed(1)}M`;
    } else if (views >= 1000) {
      return `${(views / 1000).toFixed(1)}k`;
    }
    return views.toString();
  };

  const handleViewDetails = (topic) => {
    setSelectedTopic(topic);
    setShowTopicDetails(true);
  };

  const handleClaimTopic = (topic) => {
    setClaimedTopics(prev => {
      const isClaimed = prev.find(t => t._id === topic._id);
      if (isClaimed) {
        if (selectedTopic?._id === topic._id) {
          setSelectedTopic(null);
          setShowTopicDetails(false);
        }
        return prev.filter(t => t._id !== topic._id);
      } else {
        return [topic];
      }
    });
    
    if (!claimedTopics.find(t => t._id === topic._id)) {
      setSelectedTopic(topic);
      setShowTopicDetails(true);
    }
  };

  const handleGenerateOutline = async () => {
    if (claimedTopics.length === 0) {
      alert('Please claim at least one topic first!');
      return;
    }

    if (!researchId) {
      alert('Research ID is missing. Please try again.');
      return;
    }

    setIsGenerating(true);
    try {
      const topicId = claimedTopics[0]._id;
      const outlineResponse = await dispatch(generateOutline(researchId, topicId));
      const outlineId = outlineResponse?.outlineId;
      
      navigate('/ai-outline', { 
        state: { 
          formData, 
          claimedTopics,
          selectedTopic,
          outlineId
        } 
      });
    } catch (error) {
      console.error('Error generating outline:', error);
      alert(error.message || 'Failed to generate outline. Please try again.');
      setIsGenerating(false);
    }
  };

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    dispatch(logoutUser());
    navigate('/signin');
  };

  const cancelLogout = () => {
    setShowLogoutModal(false);
  };

  return (
    <div className="h-screen bg-main flex overflow-hidden">
      {sidebarOpen && (
        <div
          className="fixed inset-0 overlay-dark z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <Sidebar
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        activePage="create-script"
        onLogout={handleLogout}
      />

      <div className="flex-1 flex flex-col h-screen overflow-hidden">
        <header className="bg-primary text-white py-4 px-6 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button 
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <MobileMenuIcon/>
              </button>
              
              <button 
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 px-4 py-2 border-2 border-white rounded-lg hover:bg-white/10 transition-colors"
              >
                <BackIcon/>
                <span className="font-medium text-sm">Back</span>
              </button>
            </div>

            <div className="flex items-center gap-3 ml-auto">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm border-2 border-white/30">
                {getUserInitials(user)}
              </div>
              <span className="text-sm font-medium hidden sm:inline">{getUserDisplayName(user)}</span>
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-6 overflow-y-auto bg-transparent relative z-10">
          <div className="bg-white rounded-2xl p-4 lg:p-5 shadow-sm">
            <div className="mb-3">
              <h1 className="text-xl lg:text-2xl font-bold text-primary">Research Results</h1>
              <p className="text-secondary text-xs lg:text-sm">Based on your data here are most relevant and high performing topics</p>
            </div>

            {researchReportState.error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm">{researchReportState.error}</p>
              </div>
            )}

            <div className="flex items-center gap-2 mb-4 mt-2">
              <SparkleIcon/>
              <span className="font-semibold text-primary">{suggestedTopics.length} Suggested Topics</span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-5">
              <div className="lg:col-span-1">
                <div className="space-y-3">
                  {suggestedTopics.length > 0 ? (
                    suggestedTopics.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((topic, index) => {
                      const actualIndex = (currentPage - 1) * itemsPerPage + index;
                      const isClaimed = claimedTopics.find(t => t._id === topic._id);
                      const isSelected = selectedTopic?._id === topic._id;
                      
                      const keywordsArray = topic.keywords?.supporting || [];
                      const mainKeyword = topic.keywords?.main || '';
                      const allKeywords = mainKeyword ? [mainKeyword, ...keywordsArray] : keywordsArray;
                      
                      return (
                        <div 
                          key={topic._id} 
                          className={`border rounded-xl p-3 transition-all ${
                            isSelected 
                              ? 'border-primary card-bg-orange shadow-md' 
                              : 'border-color-light bg-white'
                          }`}
                          onClick={() => handleViewDetails(topic)}
                        >
                          <div className="mb-2">
                            <div className="flex items-center gap-2 mb-0.5">
                              <h4 className="font-semibold text-gray-800 text-sm">Topic {actualIndex + 1}:</h4>
                              {isClaimed && <span className="status-badge-claimed">✓ Claimed</span>}
                            </div>
                            
                            <div className="h-1.5"></div>
                            
                            <h5 className="font-semibold text-gray-800 mb-3 text-base">
                              <strong>{topic.title}</strong>
                            </h5>
                            <p className="text-sm text-gray-600 mb-4">{topic.description}</p>
                          </div>

                          <div className="space-y-3 mb-4">
                            <div className="flex items-center justify-between">
                            <span className="text-sm font-bold text-gray-900">Region & Category</span>
                            <div className="flex items-center gap-1">
                              <StarrIcon className="w-4 h-4 text-gray-700 stroke-current fill-none stroke-2" />
                              <span className="text-sm font-medium">{topic.score }/10 Score</span>
                            </div>
                          </div>
                            
                            <div className="flex gap-2">
                              <span className="tag-brown">{topic.region}</span>
                              <span className="tag-brown">{topic.category}</span>
                            </div>

                            <div>
                              <span className="text-sm font-bold text-gray-900 block mb-2">Keyword</span>
                              <div className="flex gap-2 flex-wrap">
                                {allKeywords.slice(0, 3).map((keyword, idx) => (
                                  <span key={idx} className="tag-brown">{keyword}</span>
                                ))}
                              </div>
                            </div>
                          </div>

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClaimTopic(topic);
                            }}
                            className={`w-full px-4 py-3 rounded-lg font-semibold transition-colors text-sm ${
                              isClaimed 
                                ? 'bg-primary-dark text-white' 
                                : 'border border-primary-dark bg-primary-dark text-white'
                            }`}
                          >
                            {isClaimed ? 'Unclaim Topic' : 'Claim Topic & View Details'}
                          </button>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-8 text-tertiary">
                      <p>{researchReportState.loading ? 'Loading topics...' : 'No topics available'}</p>
                    </div>
                  )}
                </div>

                {suggestedTopics.length > itemsPerPage && (
                  <div className="flex items-center justify-center gap-2 mt-6 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <span className="text-sm text-gray-600">
                      Page {currentPage} of {Math.ceil(suggestedTopics.length / itemsPerPage)}
                    </span>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(Math.ceil(suggestedTopics.length / itemsPerPage), prev + 1))}
                      disabled={currentPage >= Math.ceil(suggestedTopics.length / itemsPerPage)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>

              <div className="lg:col-span-1">
              {showTopicDetails && selectedTopic ? (
                <div className="bg-white rounded-xl p-4 border border-color-light relative">
                  <button 
                    onClick={() => {
                      setShowTopicDetails(false);
                      setSelectedTopic(null);
                    }}
                    className="absolute top-3 right-3 w-6 h-6 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center text-gray-600 transition-colors"
                  >
                    ✕
                  </button>

                  <div className="mb-6">
                    <h4 className="text-xl font-bold text-primary mb-2 text-center">
                      Topic {suggestedTopics.findIndex(t => t._id === selectedTopic._id) + 1}
                    </h4>
                    <h5 className="text-lg font-semibold text-primary mb-2">
                      {selectedTopic.title}
                    </h5>
                    <p className="text-secondary text-sm">{selectedTopic.description}</p>
                  </div>

                  <div className="space-y-2 mb-6">
                    <div>
                      <span className="font-medium text-primary">Score: </span>
                      <span className="text-primary">{selectedTopic.score }/10</span>
                    </div>
                    
                    <div>
                      <span className="font-medium text-primary">Trend: </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        selectedTopic.trend === 'High' 
                          ? 'status-badge-trend-high' 
                          : selectedTopic.trend === 'Medium'
                          ? 'status-badge-trend-medium'
                          : 'bg-gray-200 text-gray-700'
                      }`}>
                        {selectedTopic.trend}
                      </span>
                    </div>
                    
                    <div>
                      <span className="font-medium text-primary">Category: </span>
                      <span className="text-primary">{selectedTopic.category}</span>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h5 className="font-bold text-primary mb-2">Competitor Videos:</h5>
                    <ul className="space-y-1 ml-4">
                      {selectedTopic.competitorVideos?.map((video, idx) => (
                        <li key={video._id || idx} className="text-secondary text-sm">
                          • {video.creator} ({formatViews(video.views)} views)
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="mb-6">
                    <h5 className="font-bold text-primary mb-2">keyword Analysis:</h5>
                    <div className="space-y-1 ml-4">
                      <div>
                        <span className="font-medium text-primary">Main: </span>
                        <span className="text-secondary text-sm">{selectedTopic.keywords?.main || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="font-medium text-primary">Supporting: </span>
                        <span className="text-secondary text-sm">
                          {selectedTopic.keywords?.supporting?.join(', ') || 'N/A'}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium text-primary">Autocomplete: </span>
                        <span className="text-secondary text-sm">
                          {selectedTopic.keywords?.autocomplete?.join(', ') || 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h5 className="font-bold text-primary mb-2">Geo Trend:</h5>
                    <div className="space-y-2">
                      {selectedTopic.geoTrend?.map((item) => {
                        const rating = Number(item.rating) || 0;
                        return (
                          <div key={item._id || item.country} className="flex items-center gap-2">
                            <span className="text-secondary text-sm w-32">{item.country}</span>
                            <div className="flex gap-0.5">
                              {Array.from({ length: rating }, (_, index) => (
                                <span key={index} className="text-yellow-400">
                                  ⭐
                                </span>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <h5 className="font-bold text-primary mb-2">Suggested Hook</h5>
                    <div className="bg-gray-100 rounded-lg p-3 mb-2">
                      <p className="text-secondary text-sm">
                        {selectedTopic.suggestedHook}
                      </p>
                    </div>
  
                  </div>
                </div>
              ) : (
                <div className="card-bg-gray rounded-xl p-4 border border-color-light h-full flex items-center justify-center">
                  <div className="text-center py-8 text-tertiary">
                    <p>Select a topic to see its details</p>
                  </div>
                </div>
              )}
            </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={handleGenerateOutline}
                disabled={claimedTopics.length === 0 || isGenerating}
                className={`font-semibold text-base text-white transition-all ${
                  claimedTopics.length === 0 || isGenerating
                    ? 'bg-gray-400 text-gray-600 cursor-not-allowed opacity-50'
                    : 'bg-[#653600] hover:bg-[#4a2800] shadow-sm'
                }`}
                style={{ width: '402px', height: '56px', borderRadius: '10px' }}
              >
                {isGenerating ? 'Generating...' : 'Generate Outline'}
              </button>
            </div>
            <LogoutModal 
              showLogoutModal={showLogoutModal}
              onConfirm={confirmLogout}
              onCancel={cancelLogout}
            />
            <LoadingModal 
              isLoading={researchReportState.loading || isGenerating}
              title={isGenerating ? 'Generating Outline' : 'Loading Research Results'}
              message={
                isGenerating 
                  ? claimedTopics.length > 0 
                    ? `For ${claimedTopics[0]?.title || 'Selected Topic'}...`
                    : 'Creating your outline...'
                  : 'Fetching research data...'
              }
            />
          </div>
        </main>
      </div>
    </div>
  );
};

export default ResearchResults;